import { createContext, useContext, useMemo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const PulseContext = /*#__PURE__*/createContext({});

/**
 * Will mount a provider for `usePulseClient`.
 * If a PulseProvider is mounted higher up in the tree, this context will use the parents' `client` value and ignore
 * the `client` provided
 *
 * Example:
 * ```tsx
 * // Basic usage
 * <PulseProvider client={myClient}>
 *   <MyComponent />
 * </PulseProvider>
 *
 * // Nested providers - child ignores its own client and inherits from parent
 * <PulseProvider client={parentClient}>
 *   <PulseProvider client={childClient}>
 *     <MyComponent /> // useResourceClient() returns parentClient
 *   </PulseProvider>
 * </PulseProvider>
 * ```
 *
 * @deprecated Pulse hooks now use a default module-level client when no
 * provider is mounted. Mounting `PulseProvider` is no longer required for
 * typical apps — drop it and the hooks will work without it.
 */
export const PulseProvider = ({
  client,
  children
}) => {
  const parentContext = useContext(PulseContext);
  const value = useMemo(() => {
    var _parentContext$client;
    return {
      client: (_parentContext$client = parentContext === null || parentContext === void 0 ? void 0 : parentContext.client) !== null && _parentContext$client !== void 0 ? _parentContext$client : client
    };
  }, [client, parentContext === null || parentContext === void 0 ? void 0 : parentContext.client]);
  return /*#__PURE__*/_jsx(PulseContext.Provider, {
    value: value,
    children: children
  });
};

/**
 * @deprecated Pulse hooks now use a default module-level client when no
 * provider is mounted, so calling `usePulseClient` is rarely necessary. If you
 * need a client reference outside of a hook, import it directly from the
 * module that creates it.
 */
export const usePulseClient = () => {
  var _useContext;
  return (_useContext = useContext(PulseContext)) === null || _useContext === void 0 ? void 0 : _useContext.client;
};